import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  MapPin,
  Building,
  Phone,
  Mail,
  User,
  Globe,
  Loader2,
  X,
  Save,
  AlertCircle,
  Sparkles,
  CheckCircle,
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { ShipFromAddress } from '../types';
import { 
  createShipFromAddress, 
  updateShipFromAddress, 
  validateShipFromAddress 
} from '../services/shipFromAddressService';

interface ShipFromAddressModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  address?: ShipFromAddress | null;
  mode: 'add' | 'edit';
}

export function ShipFromAddressModal({ 
  isOpen, 
  onClose, 
  onSuccess, 
  address, 
  mode 
}: ShipFromAddressModalProps) {
  const [formData, setFormData] = useState<Partial<ShipFromAddress>>({
    name: '',
    company: '',
    line1: '',
    line2: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'US',
    phone: '',
    email: '',
    customsSigner: '',
    isDefault: false,
  });
  const [errors, setErrors] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset form when modal opens/closes or address changes
  useEffect(() => {
    if (isOpen) {
      if (mode === 'edit' && address) {
        setFormData({
          name: address.name || '',
          company: address.company || '',
          line1: address.line1 || '',
          line2: address.line2 || '',
          city: address.city || '',
          state: address.state || '',
          zipCode: address.zipCode || '',
          country: address.country || 'US',
          phone: address.phone || '',
          email: address.email || '',
          customsSigner: address.customsSigner || '',
          isDefault: address.isDefault || false,
        });
      } else {
        setFormData({
          name: '',
          company: '',
          line1: '',
          line2: '',
          city: '',
          state: '',
          zipCode: '',
          country: 'US',
          phone: '',
          email: '',
          customsSigner: '',
          isDefault: false,
        });
      }
      setErrors([]);
    }
  }, [isOpen, address, mode]);

  const handleInputChange = (field: keyof ShipFromAddress, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear errors when user starts typing
    if (errors.length > 0) {
      setErrors([]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form data
    const validationErrors = validateShipFromAddress(formData);
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    
    try {
      if (mode === 'edit' && address?.id) {
        await updateShipFromAddress(address.id, formData);
        toast.success('Ship-from address updated successfully');
      } else {
        await createShipFromAddress(formData as Omit<ShipFromAddress, 'id' | 'createdAt' | 'updatedAt'>);
        toast.success('Ship-from address created successfully');
      }
      
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error saving ship-from address:', error);
      toast.error('Failed to save ship-from address');
    } finally {
      setIsSubmitting(false);
    }
  };

  const countries = [
    { code: 'US', name: 'United States' },
    { code: 'CA', name: 'Canada' },
    { code: 'JM', name: 'Jamaica' },
    { code: 'GB', name: 'United Kingdom' },
  ];

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-md z-[10001] flex items-center justify-center p-4" style={{ margin: 0, padding: '1rem', top: 0, left: 0, right: 0, bottom: 0 }}>
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ 
            type: "spring",
            duration: 0.5,
            bounce: 0.3 
          }}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden"
        >
          {/* Enhanced Header */}
          <div className="relative bg-gradient-to-r from-blue-500 to-cyan-500 p-6 flex-shrink-0">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-transparent"></div>
            <div className="relative flex items-center justify-between">
              <div className="flex items-center gap-4">
                <motion.div 
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: "spring", bounce: 0.6 }}
                  className="h-12 w-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/30"
                >
                  <MapPin className="h-6 w-6 text-white" />
                </motion.div>
                <div>
                  <motion.h2 
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1 }}
                    className="text-2xl font-bold text-white"
                  >
                    {mode === 'edit' ? 'Edit Ship-From Address' : 'Add Ship-From Address'}
                  </motion.h2>
                  <motion.p 
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2 }}
                    className="text-white/80 text-sm"
                  >
                    Configure your shipping origin address for EasyPost labels
                  </motion.p>
                </div>
              </div>
              <motion.div
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={onClose}
                  className="text-white hover:bg-white/20 h-10 w-10 rounded-full"
                >
                  <X className="h-5 w-5" />
                </Button>
              </motion.div>
            </div>
          </div>

          {/* Enhanced Form */}
          <div className="p-8 overflow-y-auto flex-1 bg-gradient-to-b from-gray-50/50 to-white">
            <form id="ship-from-form" onSubmit={handleSubmit} className="space-y-8">
              {/* Error Display */}
              <AnimatePresence>
                {errors.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="bg-red-50 border border-red-200 rounded-xl p-4"
                  >
                    <div className="flex items-start gap-2">
                      <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <h4 className="font-medium text-red-800 mb-1">Please fix the following errors:</h4>
                        <ul className="list-disc list-inside text-sm text-red-700 space-y-1">
                          {errors.map((error, index) => (
                            <li key={index}>{error}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Contact Information */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="space-y-6"
              >
                <h3 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                  <User className="h-5 w-5 text-blue-500" />
                  Contact Information
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <Label htmlFor="name" className="text-base font-semibold text-gray-900 flex items-center gap-2">
                      <User className="h-4 w-4 text-blue-500" />
                      Contact Name *
                    </Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      placeholder="John Doe"
                      className="h-12 rounded-xl border-gray-200 focus:border-blue-500 focus:ring-blue-500/20 shadow-sm hover:shadow-md focus:shadow-lg transition-all duration-200"
                    />
                  </div>
                  
                  <div className="space-y-3">
                    <Label htmlFor="company" className="text-base font-semibold text-gray-900 flex items-center gap-2">
                      <Building className="h-4 w-4 text-blue-500" />
                      Company Name *
                    </Label>
                    <Input
                      id="company"
                      value={formData.company}
                      onChange={(e) => handleInputChange('company', e.target.value)}
                      placeholder="JamPack'd LLC"
                      className="h-12 rounded-xl border-gray-200 focus:border-blue-500 focus:ring-blue-500/20 shadow-sm hover:shadow-md focus:shadow-lg transition-all duration-200"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <Label htmlFor="phone" className="text-base font-semibold text-gray-900 flex items-center gap-2">
                      <Phone className="h-4 w-4 text-blue-500" />
                      Phone Number *
                    </Label>
                    <div className="relative">
                      <Phone className="absolute left-4 top-4 h-5 w-5 text-gray-400" />
                      <Input
                        id="phone"
                        value={formData.phone}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                        placeholder="+1 (555) 123-4567"
                        className="pl-12 h-12 rounded-xl border-gray-200 focus:border-blue-500 focus:ring-blue-500/20 shadow-sm hover:shadow-md focus:shadow-lg transition-all duration-200"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <Label htmlFor="email" className="text-base font-semibold text-gray-900 flex items-center gap-2">
                      <Mail className="h-4 w-4 text-blue-500" />
                      Email Address
                    </Label>
                    <div className="relative">
                      <Mail className="absolute left-4 top-4 h-5 w-5 text-gray-400" />
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        placeholder="shipping@jampackd.store"
                        className="pl-12 h-12 rounded-xl border-gray-200 focus:border-blue-500 focus:ring-blue-500/20 shadow-sm hover:shadow-md focus:shadow-lg transition-all duration-200"
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <Label htmlFor="customsSigner" className="text-base font-semibold text-gray-900 flex items-center gap-2">
                    <Globe className="h-4 w-4 text-blue-500" />
                    Customs Signer Name *
                  </Label>
                  <Input
                    id="customsSigner"
                    value={formData.customsSigner}
                    onChange={(e) => handleInputChange('customsSigner', e.target.value)}
                    placeholder="Name of person who signs customs declarations"
                    className="h-12 rounded-xl border-gray-200 focus:border-blue-500 focus:ring-blue-500/20 shadow-sm hover:shadow-md focus:shadow-lg transition-all duration-200"
                  />
                  <p className="text-sm text-gray-600">
                    This person's name will appear on customs declarations for international shipments.
                  </p>
                </div>
              </motion.div>

              {/* Address Information */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="space-y-6"
              >
                <h3 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                  <Building className="h-5 w-5 text-blue-500" />
                  Address Information
                </h3>
                
                <div className="space-y-6">
                  <div className="space-y-3">
                    <Label htmlFor="line1" className="text-base font-semibold text-gray-900 flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-blue-500" />
                      Address Line 1 *
                    </Label>
                    <Input
                      id="line1"
                      value={formData.line1}
                      onChange={(e) => handleInputChange('line1', e.target.value)}
                      placeholder="123 Main Street"
                      className="h-12 rounded-xl border-gray-200 focus:border-blue-500 focus:ring-blue-500/20 shadow-sm hover:shadow-md focus:shadow-lg transition-all duration-200"
                    />
                  </div>
                  
                  <div className="space-y-3">
                    <Label htmlFor="line2" className="text-base font-semibold text-gray-900">Address Line 2</Label>
                    <Input
                      id="line2"
                      value={formData.line2}
                      onChange={(e) => handleInputChange('line2', e.target.value)}
                      placeholder="Suite 100 (optional)"
                      className="h-12 rounded-xl border-gray-200 focus:border-blue-500 focus:ring-blue-500/20 shadow-sm hover:shadow-md focus:shadow-lg transition-all duration-200"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="space-y-3">
                      <Label htmlFor="city" className="text-base font-semibold text-gray-900">City *</Label>
                      <Input
                        id="city"
                        value={formData.city}
                        onChange={(e) => handleInputChange('city', e.target.value)}
                        placeholder="New York"
                        className="h-12 rounded-xl border-gray-200 focus:border-blue-500 focus:ring-blue-500/20 shadow-sm hover:shadow-md focus:shadow-lg transition-all duration-200"
                      />
                    </div>
                    
                    <div className="space-y-3">
                      <Label htmlFor="state" className="text-base font-semibold text-gray-900">State/Province *</Label>
                      <Input
                        id="state"
                        value={formData.state}
                        onChange={(e) => handleInputChange('state', e.target.value)}
                        placeholder="NY"
                        className="h-12 rounded-xl border-gray-200 focus:border-blue-500 focus:ring-blue-500/20 shadow-sm hover:shadow-md focus:shadow-lg transition-all duration-200"
                      />
                    </div>
                    
                    <div className="space-y-3">
                      <Label htmlFor="zipCode" className="text-base font-semibold text-gray-900">ZIP/Postal Code *</Label>
                      <Input
                        id="zipCode"
                        value={formData.zipCode}
                        onChange={(e) => handleInputChange('zipCode', e.target.value)}
                        placeholder="10001"
                        className="h-12 rounded-xl border-gray-200 focus:border-blue-500 focus:ring-blue-500/20 shadow-sm hover:shadow-md focus:shadow-lg transition-all duration-200"
                      />
                    </div>
                  </div>

                  <div className="space-y-3">
                    <Label htmlFor="country" className="text-base font-semibold text-gray-900 flex items-center gap-2">
                      <Globe className="h-4 w-4 text-blue-500" />
                      Country *
                    </Label>
                    <Select value={formData.country} onValueChange={(value) => handleInputChange('country', value)}>
                      <SelectTrigger className="h-12 rounded-xl border-gray-200 focus:border-blue-500 focus:ring-blue-500/20 shadow-sm hover:shadow-md transition-all duration-200">
                        <SelectValue placeholder="Select country" />
                      </SelectTrigger>
                      <SelectContent>
                        {countries.map((country) => (
                          <SelectItem key={country.code} value={country.code}>
                            {country.name} ({country.code})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </motion.div>

              {/* Default Address Setting */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-6 border border-blue-100"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 bg-blue-500/20 rounded-full flex items-center justify-center">
                      <CheckCircle className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <label className="text-base font-semibold text-gray-900">Set as Default Address</label>
                      <p className="text-sm text-gray-600 mt-1">
                        Use this address as the default ship-from address for EasyPost labels
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={formData.isDefault}
                    onCheckedChange={(checked) => handleInputChange('isDefault', checked)}
                  />
                </div>
              </motion.div>
            </form>
          </div>

          {/* Enhanced Footer */}
          <div className="p-6 bg-gray-50 border-t border-gray-100 flex-shrink-0">
            <div className="flex items-center justify-end gap-4">
              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <Button 
                  type="button"
                  variant="outline" 
                  onClick={onClose}
                  disabled={isSubmitting}
                  className="px-6 h-11 rounded-xl font-medium"
                >
                  Cancel
                </Button>
              </motion.div>
              
              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <Button 
                  type="submit"
                  form="ship-from-form"
                  disabled={isSubmitting}
                  className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white px-8 h-11 rounded-xl font-semibold shadow-lg shadow-blue-500/25"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {mode === 'edit' ? 'Updating...' : 'Creating...'}
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      {mode === 'edit' ? 'Update Address' : 'Create Address'}
                    </>
                  )}
                </Button>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}